import { useState } from 'react';
import { useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { useSnackbar } from 'notistack';
import { FormattedMessage, useIntl } from 'react-intl';
import Tooltip from '@mui/material/Tooltip';
import { useLaunchDarkly } from '../../../../launchdarkly';
import { ToggleRenewalConfirmDialog } from './ToggleRenewalConfirmDialog';
import { fromPaymentMethod, fromSubscription, fromSurvey, useAppDispatch } from '../../../../store';
import TurnOffAutoRenewDialog from './TurnOffAutoRenewDialog';
import { PaymentMethodType, SurveyName } from '../../../../models';
import { useReadOnlyMode } from '../../../../routes';
import CustomSwitch from '../../../../components/switch/CustomSwitch';

export const ToggleRenewal = (
  {
    productName,
    autoRenewal,
    recurlySubscriptionId,
    setAutoRenew,
    disabled,
    onOpenDialog,
    onCancelDialog,
    onConfirmDialog,
    expiryDate,
    billingCycleLabel,
    isTrueBundle,
  }: {
    productName: string;
    autoRenewal: boolean;
    recurlySubscriptionId: number;
    setAutoRenew: Function;
    disabled?: boolean;
    onOpenDialog?: () => void;
    onCancelDialog?: () => void;
    onConfirmDialog?: () => void;
    expiryDate: Date;
    billingCycleLabel?: string;
    isTrueBundle?: boolean;
  },
) => {
  const { flags } = useLaunchDarkly();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<'survey' | 'simple'>('simple');
  const intl = useIntl();
  const currentDefaultPaymentMethod = useSelector(fromPaymentMethod.selectDefaultPaymentMethod);
  const isReadOnly = useReadOnlyMode();
  const isAwsMarketplace = currentDefaultPaymentMethod === PaymentMethodType.AWS_MARKETPLACE;
  const disableSwitch = isReadOnly || isAwsMarketplace || disabled;
  const toggleAutoRenew = () => {
    onConfirmDialog?.();
    setOpenDialog(false);
    turnOffAutoRenew();
  };

  const turnOffAutoRenew = () => {
    dispatch(fromSubscription.doToggleAutoRenew(recurlySubscriptionId))
      .then(unwrapResult)
      .then(autoRenew => {
        setAutoRenew(autoRenew);
      })
      .catch(() => {
        enqueueSnackbar(
          intl.formatMessage(
            { id: 'page.subscription.toggle.auto.renew.error' },
            { action: autoRenewal ? 'turn off' : 'turn on' },
          ),
          { variant: 'error' },
        );
      });
  };

  const getSurvey = async (productName: string) => {
    if (flags?.turnOffAutoRenewSurveyEnabled) {
      try {
        const result = await dispatch(fromSurvey.doGetSurvey({
          context: SurveyName.TURN_OFF_AUTO_RENEW_SURVEY,
          productName: intl.formatMessage({ id: `feature.${productName}.product.name` }),
          expiryDate: new Date(expiryDate.valueOf()).toISOString().slice(0, 10),
          recurlySubscriptionId,
        }));
        return unwrapResult(result);
      } catch (error) {
        return null;
      }
    }
    return null;
  };

  const handleCloseDialog = () => {
    onCancelDialog?.();
    setOpenDialog(false);
  };

  const handleOpenDialog = async () => {
    if (!autoRenewal) {
      setDialogType('simple');
    } else {
      // Get survey data first
      const surveyResult = await getSurvey(productName);

      // After getting the survey, check if we should show survey dialog
      const shouldShowSurvey = flags?.turnOffAutoRenewSurveyEnabled && autoRenewal
        && surveyResult && surveyResult.surveyConfig && surveyResult.surveyConfig.surveyURL;

      setDialogType(shouldShowSurvey ? 'survey' : 'simple');
    }
    onOpenDialog?.();
    setOpenDialog(true);
  };

  const renderConfirmDialog = () => (
    // using the survey URL to show the survey
    // when the flag is turned on and the survey is available,
    // the survey will only show in case of turn off auto-renewal
    dialogType === 'survey'
      ? (
        <TurnOffAutoRenewDialog
          showDialog={openDialog}
          handleCloseDialog={handleCloseDialog}
          toggleAutoRenew={toggleAutoRenew}
        />
      )
      : (
        <ToggleRenewalConfirmDialog
          productName={productName}
          autoRenew={autoRenewal}
          open={openDialog}
          onClose={handleCloseDialog}
          toggleAutoRenew={toggleAutoRenew}
          expiryDate={expiryDate}
          billingCycleLabel={billingCycleLabel}
          isTrueBundle={isTrueBundle}
        />
      )
  );

  const renderSwitchAutoRenew = () => (
    <CustomSwitch
      disabled={disableSwitch}
      checked={autoRenewal}
      onChange={handleOpenDialog}
    />
  );

  return (
    <>
      {isAwsMarketplace ? (
        <Tooltip
          title={(
            <FormattedMessage id="page.subscription.toggle.auto.renew.aws.marketplace" />
          )}
          placement="top"
        >
          <span>
            {renderSwitchAutoRenew()}
          </span>
        </Tooltip>
      ) : (
        renderSwitchAutoRenew()
      )}
      {renderConfirmDialog()}
    </>
  );
};
